import React from 'react';
import { Link } from '@tanstack/react-router';
import { Tag } from 'antd';
import type { NavigationItem } from '@/utils/navigationTypes';

/**
 * Generates a navigation item for sidebar navigation, providing consistent styling and structure.
 *
 * @param key                   Unique key for the navigation item (used for React rendering and identification)
 * @param icon                  React node representing the icon to display next to the label
 * @param label                 Human-readable string label to display in the navigation sidebar
 * @param href                  Destination route path (used for routing with TanStack Router's <Link>)
 * @param isMocked              (Optional) If true, prepends a "Mocked" blue tag to visually distinguish mocked/in-development items.
 * @param requiredTenantRoles   (Optional) If set, item is only shown when user has at least one of these tenant roles (or is global ADMIN).
 * @param search                (Optional) TanStack Router search params for the destination route.
 * @returns                     NavigationItem object compatible with the SidebarLayout and navigation group structures.
 *
 * ## Design Notes
 * - This helper enforces a uniform appearance: font size, icon-to-label gap, and consistent use of <Link> for SPA navigation.
 * - If `isMocked` is true, a Tag component with "Mocked" appears before the label (useful for features under development or demo).
 * - The result object structure matches the signature expected by navigation arrays and children of group navigation items.
 *
 * ## Example Usage
 * ```
 * createNavItem('users', <UserOutlined />, 'Users', '/admin/users')
 * createNavItem('ext-jobs', <SolutionOutlined />, 'External Jobs', '/manage/jobs', true)
 * ```
 */
export const createNavItem = (
  key: string,
  icon: React.ReactNode,
  label: string,
  href: string,
  isMocked: boolean = false,
  requiredTenantRoles?: string[],
  search?: Record<string, unknown>
): NavigationItem => ({
  key,
  icon,
  labelFallback: label,
  isMocked,
  label: (
    <Link to={href} search={search as never} className="flex items-center gap-2" style={{ fontSize: '14px' }}>
      {/* Display "Mocked" tag if the item is flagged as a mock or in-progress feature */}
      {isMocked && <Tag color="blue">Mocked</Tag>}
      {label}
    </Link>
  ),
  href,
  ...(requiredTenantRoles && { requiredTenantRoles })
});
